import { RpcEngine } from '../core/rpc.js'
import { METHODS, PUSH } from '../core/events.js'
import type { VariableMap, Unsubscribe } from '../types/index.js'

export class VariableModule {
  private observedValues = new Map<string, unknown>()

  constructor(private rpc: RpcEngine) {}

  getAll():                                 Promise<VariableMap> { return this.rpc.call(METHODS.VARIABLE_GET_ALL) }
  get<T = unknown>(key: string):            Promise<T | null>    {
    return this.rpc.call<T | null | undefined>(METHODS.VARIABLE_GET, [key]).then(value => value ?? null)
  }
  set<T = unknown>(key: string, value: T):  Promise<void>        { return this.rpc.call(METHODS.VARIABLE_SET, [key, value]) }
  setMany(vars: VariableMap):               Promise<void>        { return this.rpc.call(METHODS.VARIABLE_SET_MANY, [vars]) }
  increment(key: string, delta = 1):        Promise<number>      { return this.rpc.call(METHODS.VARIABLE_INCREMENT, [key, delta]) }
  decrement(key: string, delta = 1):        Promise<number>      { return this.rpc.call(METHODS.VARIABLE_DECREMENT, [key, delta]) }
  delete(key: string):                      Promise<void>        { return this.rpc.call(METHODS.VARIABLE_DELETE, [key]) }
  clear():                                  Promise<void>        { return this.rpc.call(METHODS.VARIABLE_CLEAR) }

  /**
   * Watch a single variable for changes pushed from the platform.
   * Requires the platform to send `{ type: '__charx_push__', event: 'variable.changed', data: { key, value } }`.
   */
  watch<T = unknown>(key: string, callback: (value: T, oldValue: T | undefined) => void): Unsubscribe {
    let previousValue = this.observedValues.get(key) as T | undefined
    return this.rpc.push(PUSH.VARIABLE_CHANGED, (data) => {
      if (!data || typeof data !== 'object') return
      const payload = data as { key?: unknown; value?: unknown } & VariableMap
      if (typeof payload.key === 'string' && payload.key === key) {
        this.observedValues.set(key, payload.value)
        callback(payload.value as T, previousValue)
        previousValue = payload.value as T
        return
      }
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        this.observedValues.set(key, payload[key])
        callback(payload[key] as T, previousValue)
        previousValue = payload[key] as T
      }
      for (const [payloadKey, value] of Object.entries(payload)) {
        if (payloadKey !== 'key' && payloadKey !== 'value') {
          this.observedValues.set(payloadKey, value)
        }
      }
    })
  }

  /**
   * Watch all variables — callback receives the full variable map on any change.
   * Re-fetches the complete map after each push event so the callback always
   * receives a consistent snapshot (the push payload is a single { key, value },
   * not the full map).
   */
  watchAll(callback: (vars: VariableMap) => void): Unsubscribe {
    return this.rpc.push(PUSH.VARIABLE_CHANGED, () => {
      this.getAll().then(callback).catch(() => {})
    })
  }
}
